/**
 * Shared upload file-type configuration.
 *
 * Keeps the MIME allow-list, the file input `accept` attribute, and the
 * user-facing copy in one place so the single-file picker and the multi-file
 * upload form stay in sync.
 */

/** Which kinds of files an upload control accepts. */
export type FileAccept = 'image' | 'pdf' | 'image-or-pdf' | 'all';

export interface AcceptConfig {
  /** Allowed MIME types. `null` means any file is allowed. */
  types: Set<string> | null;
  /** Value for the file input `accept` attribute. `undefined` means no restriction. */
  attr: string | undefined;
  /** Short label shown in the dropzone hint (e.g. "PNG, JPG"). */
  label: string;
  /** Error message shown when a file of the wrong type is selected. */
  errorMessage: string;
  /** Singular noun for the accepted files, used in labels and toasts. */
  noun: string;
  /** Plural noun for the accepted files, used in labels and toasts. */
  nounPlural: string;
}

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const PDF_TYPE = 'application/pdf';

export const ACCEPT_CONFIG: Record<FileAccept, AcceptConfig> = {
  'image': {
    types: new Set(IMAGE_TYPES),
    attr: IMAGE_TYPES.join(','),
    label: 'PNG, JPG, GIF, WebP',
    errorMessage: 'File must be a PNG, JPG, GIF, or WebP image.',
    noun: 'image',
    nounPlural: 'images',
  },
  'pdf': {
    types: new Set([PDF_TYPE]),
    attr: PDF_TYPE,
    label: 'PDF',
    errorMessage: 'File must be a PDF.',
    noun: 'PDF',
    nounPlural: 'PDFs',
  },
  'image-or-pdf': {
    types: new Set([...IMAGE_TYPES, PDF_TYPE]),
    attr: [...IMAGE_TYPES, PDF_TYPE].join(','),
    label: 'PNG, JPG, GIF, WebP, PDF',
    errorMessage: 'File must be a PNG, JPG, GIF, or WebP image, or a PDF.',
    noun: 'file',
    nounPlural: 'files',
  },
  'all': {
    types: null,
    attr: undefined,
    label: 'Any file',
    errorMessage: '',
    noun: 'file',
    nounPlural: 'files',
  },
};

/** Whether a file passes the MIME allow-list for the given accept mode. */
export function isAcceptedFileType(file: File, accept: FileAccept): boolean {
  const { types } = ACCEPT_CONFIG[accept];
  return !types || types.has(file.type);
}
